/**
 * Non-blocking notifications, in place of `window.alert()`.
 *
 * `alert()` froze the whole tab until it was dismissed — status polls and all —
 * could not be styled, and looked like the browser rather than the app. Call
 * `toast.error(...)` (or `.success`, `.warning`, `.info`) from any handler; the
 * one `<Toaster />` in the app shell renders them. The store is a module, not
 * a React context, so a toast can be raised from any callback without
 * threading a hook through every component.
 *
 * With no `<Toaster />` mounted (a component rendered alone in a unit test)
 * toasts simply collect in the store — `getToasts()` lets a test read them.
 */

export type ToastTone = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
    id: number
    tone: ToastTone
    message: string
    title?: string
}

export interface ToastOptions {
    title?: string
    /** Milliseconds before it closes itself; 0 keeps it until dismissed. */
    duration?: number
}

const MAX_VISIBLE = 4
const DEFAULT_DURATION: Record<ToastTone, number> = {
    success: 4000,
    info: 5000,
    warning: 7000,
    error: 9000,
}

let toasts: Toast[] = []
let nextId = 1
const listeners = new Set<() => void>()
const timers = new Map<number, ReturnType<typeof setTimeout>>()

function emit() {
    for (const listener of listeners) listener()
}

export function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export function getToasts(): Toast[] {
    return toasts
}

export function dismissToast(id: number) {
    const timer = timers.get(id)
    if (timer) clearTimeout(timer)
    timers.delete(id)
    const next = toasts.filter((item) => item.id !== id)
    if (next.length !== toasts.length) {
        toasts = next
        emit()
    }
}

/** Remove every toast — for tests. */
export function clearToasts() {
    for (const timer of timers.values()) clearTimeout(timer)
    timers.clear()
    toasts = []
    emit()
}

function push(tone: ToastTone, message: string, options: ToastOptions = {}): number {
    const id = nextId++
    // The same message twice in a row (a double click) shows once.
    const last = toasts[toasts.length - 1]
    if (last && last.tone === tone && last.message === message) {
        dismissToast(last.id)
    }
    toasts = [...toasts, { id, tone, message, title: options.title }].slice(-MAX_VISIBLE)
    emit()
    const duration = options.duration ?? DEFAULT_DURATION[tone]
    if (duration > 0) {
        timers.set(
            id,
            setTimeout(() => dismissToast(id), duration),
        )
    }
    return id
}

export const toast = {
    success: (message: string, options?: ToastOptions) => push('success', message, options),
    error: (message: string, options?: ToastOptions) => push('error', message, options),
    warning: (message: string, options?: ToastOptions) => push('warning', message, options),
    info: (message: string, options?: ToastOptions) => push('info', message, options),
}
